class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.productEl = this.querySelector('[data-cart-notification-product]');
    this.titleEl = this.querySelector('[data-cart-notification-title]');
    this.closeButtons = this.querySelectorAll('[data-cart-notification-close]');

    this.autoCloseDelay = (parseInt(this.dataset.autoClose, 10) || 5) * 1000;

    this.onAdded = this.handleAdded.bind(this);
    this.onKeyup = this.handleKeyup.bind(this);
    this.onPause = this.pauseTimer.bind(this);
    this.onResume = this.resumeTimer.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.addEventListener('mouseenter', this.onPause);
    this.addEventListener('mouseleave', this.onResume);
    this.addEventListener('focusin', this.onPause);
    this.addEventListener('focusout', this.onResume);
  }

  connectedCallback() {
    document.addEventListener('product-ajax:added', this.onAdded);
    document.addEventListener('keyup', this.onKeyup);
  }

  disconnectedCallback() {
    document.removeEventListener('product-ajax:added', this.onAdded);
    document.removeEventListener('keyup', this.onKeyup);
    document.removeEventListener('click', this.onDocumentClick);
    this.clearTimer();
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.close();
  }

  handleKeyup(event) {
    if (event.code === 'Escape' && this.classList.contains('active')) this.close();
  }

  handleAdded(event) {
    const product = event.detail && event.detail.product;
    if (!product) return;

    this.renderProduct(product);
    this.open();
  }

  renderProduct(product) {
    this.productEl.innerHTML = '';

    if (Array.isArray(product.items)) {
      this.renderBundle(product.items);
    } else {
      this.renderSingleItem(product);
    }
  }

  renderSingleItem(item) {
    this.titleEl.textContent = 'Added to your cart';

    const row = document.createElement('div');
    row.className = 'cart-notification__item flex items-start gap-3';

    if (item.image) {
      const media = document.createElement('div');
      media.className = 'cart-notification__media';
      const img = document.createElement('img');
      img.src = this.getResizedImageUrl(item.image, 120);
      img.alt = item.product_title || item.title || '';
      img.width = 60;
      img.height = 60;
      img.loading = 'lazy';
      media.appendChild(img);
      row.appendChild(media);
    }

    const details = document.createElement('div');
    details.className = 'cart-notification__details';

    const titleLine = document.createElement('p');
    titleLine.className = 'cart-notification__product-title';
    titleLine.textContent = item.product_title || item.title;
    details.appendChild(titleLine);

    if (item.variant_title && item.product_has_only_default_variant === false) {
      const variantLine = document.createElement('p');
      variantLine.className = 'cart-notification__variant-title text-sm text-subtext';
      variantLine.textContent = item.variant_title;
      details.appendChild(variantLine);
    }

    const meta = document.createElement('div');
    meta.className = 'cart-notification__meta flex items-center justify-between';

    const qty = document.createElement('span');
    qty.className = 'cart-notification__qty text-sm text-subtext';
    qty.textContent = `Qty: ${item.quantity || 1}`;
    meta.appendChild(qty);

    const price = document.createElement('span');
    price.className = 'cart-notification__price font-body-bolder';
    price.textContent = this.formatMoney(item.final_price);
    meta.appendChild(price);

    details.appendChild(meta);
    row.appendChild(details);

    this.productEl.appendChild(row);
  }

  renderBundle(items) {
    const totalQty = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
    this.titleEl.textContent = `${totalQty} items added to your cart`;

    const list = document.createElement('div');
    list.className = 'cart-notification__thumbs flex items-center gap-2';

    items.slice(0, 4).forEach((item) => {
      if (!item.image) return;
      const img = document.createElement('img');
      img.className = 'cart-notification__thumb blocks-radius-sm';
      img.src = this.getResizedImageUrl(item.image, 100);
      img.alt = item.product_title || item.title || '';
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      list.appendChild(img);
    });

    if (items.length > 4) {
      const more = document.createElement('span');
      more.className = 'cart-notification__thumb-more text-sm text-subtext';
      more.textContent = `+${items.length - 4}`;
      list.appendChild(more);
    }

    this.productEl.appendChild(list);
  }

  getResizedImageUrl(url, width) {
    try {
      const resized = new URL(url, window.location.origin);
      resized.searchParams.set('width', width);
      return resized.toString();
    } catch (error) {
      return url;
    }
  }

  formatMoney(cents) {
    if (window.FoxTheme && window.FoxTheme.Currency && typeof window.FoxTheme.Currency.formatMoney === 'function') {
      return window.FoxTheme.Currency.formatMoney(cents, window.FoxTheme.settings.moneyFormat);
    }
    return cents;
  }

  open() {
    this.clearTimer();
    this.hidden = false;
    // Force a reflow so the opening transition runs on the next frame.
    void this.offsetHeight;
    this.classList.add('active');
    this.startTimer();
    document.addEventListener('click', this.onDocumentClick);
  }

  close() {
    this.clearTimer();
    this.classList.remove('active');
    document.removeEventListener('click', this.onDocumentClick);
    this.hideTimeout = setTimeout(() => {
      this.hidden = true;
    }, 300);
  }

  startTimer() {
    this.timer = setTimeout(() => this.close(), this.autoCloseDelay);
  }

  pauseTimer() {
    if (!this.classList.contains('active')) return;
    clearTimeout(this.timer);
    this.timer = null;
  }

  resumeTimer() {
    if (!this.classList.contains('active') || this.timer) return;
    this.startTimer();
  }

  clearTimer() {
    clearTimeout(this.timer);
    this.timer = null;
    clearTimeout(this.hideTimeout);
  }
}

customElements.define('cart-notification', CartNotification);
